import { useContext, useState } from "react";
import { Tab, Typography, Grid, Box } from "@mui/material";
import { UserContext } from "../../contexts/userContext";
import {
  ECOMMERCE,
  MARKETPLACE,
  MERCHANT,
  INTERNAL,
  OWNER_ROLE_NAME,
} from "../../utils/constants";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import Carriers from "../../components/configuration/CarriersContainer";
import Rules from "../../components/configuration/RulesContainer";
import WebhookSettings from "../../components/configuration/WebhookSettings";
import { IUserRole } from "../../interfaces/userContext";
import FeesTab from "../../components/tabs/FeesTabContainer";
import PostmarkAccountSettings from "../../components/configuration/PostmarkAccountSettings";

const CONFIGURATION_TABS_BASE = [
  {
    id: "carriers",
    label: "Operadores Logísticos",
    component: <Carriers />,
  },
  {
    id: "permissions",
    label: "Permisos",
    component: (
      <Grid lg={4} className="m-t-30">
        <Rules />
      </Grid>
    ),
  },
  {
    id: "webhook",
    label: "Notificación de estados",
    component: (
      <Grid lg={4} className="m-t-30">
        <WebhookSettings />
      </Grid>
    ),
  },
  {
    id: "fees",
    label: "Tarifa plana",
    component: <FeesTab />,
  },
  {
    id: "postmark-settings",
    label: "Configuración de Postmark",
    component: <Grid lg={4} className="m-t-30"><PostmarkAccountSettings /></Grid>,
  },
];

const getConfigurationTabs = () => {
  const channel = process.env.REACT_APP_CHANNEL;
  if (channel === "macro") {
    // Excluir el tab de PostmarkAccountSettings
    return CONFIGURATION_TABS_BASE.filter((tab) => tab.id !== "postmark-settings");
  }
  // Por defecto (incluye "coati"), mostrar todas
  return CONFIGURATION_TABS_BASE;
};

const getBlacklistedTabs = (entity: string): string[] => {
  switch (entity) {
  case ECOMMERCE:
    return ["permissions"];
  case MERCHANT:
  case INTERNAL:
    return ["webhook", "permissions", "fees"];
  case MARKETPLACE:
    return ["own-fleet"];
  default:
    return [];
  }
};

const getTabs = (entity: string, roles: IUserRole[]) => {
  const blacklist = getBlacklistedTabs(entity);
  const configurationTabs = getConfigurationTabs();
  const tabs = configurationTabs.filter(({ id }) => {
    // Webhook and Postmark settings tabs should only be available for marketplace and ecommerce entities with owner role
    if (id === "webhook" || id === "postmark-settings") {
      return (entity === MARKETPLACE || entity === ECOMMERCE) && roles.some(({ name }) => name === OWNER_ROLE_NAME);
    }
    // Para otros casos, filtra por blacklist
    return !blacklist.includes(id);
  });
  return tabs;
};

const Configuration = () => {
  const [activeTab, setActiveTab] = useState("carriers");
  const {
    currentUser: { entity, roles },
  } = useContext(UserContext);

  const tabs = getTabs(entity, roles);
  return (
    <Grid container flexDirection="column">
      <Typography className="f-s-20">Configuración</Typography>

      <TabContext value={activeTab}>
        <Box
          className="m-t-20"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            width: "fit-content",
          }}
        >
          <TabList onChange={(_, newValue) => setActiveTab(newValue)}>
            {tabs.map(({ id, label }) => (
              <Tab key={id} value={id} label={label} />
            ))}
          </TabList>
        </Box>
        {tabs.map(({ id, component }, index) => (
          <TabPanel key={index} value={id}>
            {component}
          </TabPanel>
        ))}
      </TabContext>
    </Grid>
  );
};

export default Configuration;
